import * as Sentry from '@sentry/react';
import { ZodError } from 'zod';
import { InvalidDataFoundError, NoDataFoundError } from './APIRequestError';
import { User } from 'interfaces';

interface HandleSentryErrorsParams {
  error: Error | ZodError | InvalidDataFoundError | NoDataFoundError;
  functionName: string;
  params: any;
  displayNotification: boolean;
  handleNoDataFound?: (functionName: string) => void;
  handleInvalidData?: (functionName: string, message: string) => void;
  user?: User;
}

const serializeForSentry = (data: any): string => {
  try {
    return JSON.stringify(data, null, 2);
  } catch (error) {
    return `Serialization error: ${error.message}`;
  }
};

export const handleSentryErrors = ({
  error,
  functionName,
  params,
  displayNotification = false,
  handleNoDataFound,
  handleInvalidData,
  user,
}: HandleSentryErrorsParams): Error | void => {
  if (displayNotification) {
    if (error instanceof NoDataFoundError && handleNoDataFound) {
      handleNoDataFound(functionName);
    } else if (error instanceof InvalidDataFoundError && handleInvalidData) {
      handleInvalidData(functionName, 'Invalid data');
    }
  }
  if (error instanceof NoDataFoundError) {
    throw error;
  }
  Sentry.withScope((scope) => {
    if (user)
      scope.setUser({
        email: user.email,
        id: user.id,
        username: user.cognitoID,
      });
    scope.setTag('functionName', functionName);
    if (params && Object.keys(params).length > 0) {
      scope.setContext('params', {
        serializedParams: serializeForSentry(params),
      });
    }
    if (error instanceof InvalidDataFoundError) {
      scope.setFingerprint([functionName, 'InvalidDataFoundError']);
      scope.setLevel('error');
      scope.setContext('Validation Errors', {
        validationErrors: serializeForSentry(error.validationErrors),
      });
      scope.setContext('Invalid Data', {
        invalidData: serializeForSentry(error.data),
      });
      Sentry.captureException(error);
    } else {
      // Unknown or other errors
      scope.setFingerprint([functionName, error.name || 'UnknownError']);
      scope.setLevel('error');
      scope.setExtra('Error Message', error.message);
      Sentry.captureException(error);
    }
  });

  if (!(error instanceof InvalidDataFoundError)) {
    throw error;
  }
};
